/**
 * ProgressoPipeline — indicador SEGMENTADO POR ETAPA (stepper) do pipeline.
 *
 * Modelo "moderno" único do app: uma barra dividida em SEGMENTOS, um por etapa do
 * pipeline (Preparo → Triagem → Mapa → Blocos → Anti-IA), cada segmento com 3
 * estados claros — ✓ concluída · ● ativa (com sub-preenchimento + shimmer) ·
 * ○ pendente. A POSIÇÃO do segmento ativo é o sinal principal; o `pct` preenche
 * só o segmento ativo. Boas práticas p/ operações longas (Apple HIG / Material /
 * NN-g): cronômetro de liveness + status ao vivo + reassurance.
 *
 *  • variant 'full'   — card completo (modal / refino): segmentos + rótulos +
 *                       cronômetro + status + reassurance.
 *  • variant 'compact'— linha fina de segmentos + status (cabe no card do Kanban).
 */
import { useEffect, useState } from 'react';
import type { PecasStatus } from '../types';

type Etapa = NonNullable<PecasStatus['progresso_etapa']>;

interface Props {
  etapa: Etapa | null;
  pct: number | null;
  msg: string | null;
  compact?: boolean;
  /** Staging (Novo Prazo): só roda o preparo — usa segmentos próprios (sem
   * Mapa/Redação/Revisão, que nunca enchem nesse fluxo). */
  staging?: boolean;
}

type Seg = { key: string; label: string; etapas: Etapa[] };

// Pipeline de geração da peça (Stage A). Triagem e Anti-IA são sub-fases (a etapa
// crua é 'pack'/'blocos') detectadas pela mensagem — por isso `etapas: []`.
const SEG_PROCESSO: Seg[] = [
  { key: 'preparo', label: 'Leitura',    etapas: ['split', 'ocr', 'pdftotext', 'classify', 'pack'] },
  { key: 'triagem', label: 'Análise',    etapas: [] },
  { key: 'mapa',    label: 'Estratégia', etapas: ['mapa'] },
  { key: 'blocos',  label: 'Redação',    etapas: ['blocos'] },
  { key: 'antiia',  label: 'Revisão',    etapas: [] },
];
// Staging do "Novo Prazo": só o preparo (split/OCR/pdftotext/classify/pack). 3
// segmentos que realmente enchem — nada de segmentos mortos.
const SEG_STAGING: Seg[] = [
  { key: 'separar', label: 'Separando peças', etapas: ['split'] },
  { key: 'lendo',   label: 'Lendo (OCR)',     etapas: ['ocr', 'pdftotext'] },
  { key: 'analise', label: 'Analisando',      etapas: ['classify', 'pack'] },
];
const SEG_PETICAO: Seg[]  = [{ key: 'peticao',  label: 'Redigindo a petição',    etapas: ['peticao'] }];
const SEG_SALVANDO: Seg[] = [{ key: 'salvando', label: 'Versionando a petição',  etapas: ['salvando'] }];
const SEG_RENDER: Seg[]   = [{ key: 'render',   label: 'Regerando a petição',    etapas: ['render'] }];

const clamp = (n: number) => Math.max(0, Math.min(100, n));

function fmtTempo(seg: number): string {
  const m = Math.floor(seg / 60);
  return `${m}:${String(seg % 60).padStart(2, '0')}`;
}

/** Índice do segmento ativo: triagem/anti-IA vêm pela msg; o resto pela etapa crua. */
function indiceAtivo(segs: Seg[], etapa: Etapa | null, msg: string | null): number {
  const m = (msg || '').toLowerCase();
  if (segs === SEG_PROCESSO) {
    if (/analisando o caso|triagem/.test(m)) return 1;
    if (/revisando|anti.?ia|refinando/.test(m)) return 4;
  }
  const i = etapa ? segs.findIndex((s) => s.etapas.includes(etapa)) : 0;
  return i < 0 ? 0 : i;
}

export function ProgressoPipeline({ etapa, pct, msg, compact = false, staging = false }: Props) {
  const [elapsed, setElapsed] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setElapsed((e) => e + 1), 1000);
    return () => clearInterval(id);
  }, []);

  const segs = staging          ? SEG_STAGING
             : etapa === 'salvando' ? SEG_SALVANDO
             : etapa === 'peticao'  ? SEG_PETICAO
             : etapa === 'render'   ? SEG_RENDER
             : SEG_PROCESSO;
  const titulo = staging          ? 'Lendo os autos'
               : etapa === 'salvando' ? 'Salvando e versionando'
               : etapa === 'peticao'  ? 'Gerando a petição final'
               : etapa === 'render'   ? 'Regerando a petição'
               : 'Gerando a peça';

  const idx = indiceAtivo(segs, etapa, msg);
  const subFill = Math.max(8, clamp(pct ?? 0));   // preenchimento DENTRO do segmento ativo
  const statusMsg = (msg || '').replace(/\s*—\s*\d+s\s*$/, '') || `${segs[idx].label}…`;

  // ── Trilha de segmentos (comum aos dois variants) ──
  const trilha = (
    <div className={`flex ${compact ? 'gap-1' : 'gap-1.5'}`}>
      {segs.map((s, i) => {
        const done = i < idx;
        const active = i === idx;
        return (
          <div
            key={s.key}
            className={`relative flex-1 overflow-hidden rounded-full ${compact ? 'h-1.5' : 'h-2'} ${
              done ? 'bg-emerald-500'
              : active ? 'bg-blue-100 dark:bg-blue-950/60'
              : 'bg-slate-200 dark:bg-slate-700'
            }`}
          >
            {active && (
              <div className="absolute inset-y-0 left-0 rounded-full bg-blue-500 transition-[width] duration-700 ease-out"
                   style={{ width: `${subFill}%` }}>
                <span className="pp-sweep absolute inset-y-0 w-1/3 bg-white/45 rounded-full" />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );

  // ── Variant COMPACT (card do Kanban) ──
  if (compact) {
    return (
      <div className="text-[10px]">
        <div className="flex items-center justify-between mb-1 text-slate-500 dark:text-slate-400">
          <span className="truncate font-semibold text-blue-700 dark:text-blue-300">
            <span className="inline-block animate-spin mr-1">◠</span>{statusMsg}
          </span>
          {typeof pct === 'number' && <span className="font-mono ml-1 shrink-0">{pct}%</span>}
        </div>
        {trilha}
        {segs.length > 1 && (
          <div className="mt-1 flex justify-between text-[9px] text-slate-400 dark:text-slate-500">
            {segs.map((s, i) => (
              <span key={s.key} className={i === idx ? 'text-blue-600 dark:text-blue-400 font-medium' : i < idx ? 'text-emerald-600 dark:text-emerald-400' : ''}>
                {i < idx ? '✓' : ''}{s.label}
              </span>
            ))}
          </div>
        )}
      </div>
    );
  }

  // ── Variant FULL (modal / refino) ──
  return (
    <div className="mt-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900/60 p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2 min-w-0">
          <span className="relative flex h-2.5 w-2.5 shrink-0">
            <span className="absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-60 animate-ping" />
            <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-blue-500" />
          </span>
          <span className="text-xs font-semibold tracking-wide text-slate-700 dark:text-slate-200 truncate">{titulo}</span>
        </div>
        <span className="shrink-0 font-mono tabular-nums text-sm font-bold text-blue-700 dark:text-blue-300 bg-blue-50 dark:bg-blue-500/10 border border-blue-200 dark:border-blue-400/20 rounded-md px-2 py-0.5"
              title="Tempo decorrido — se está subindo, está rodando" aria-live="polite">
          {fmtTempo(elapsed)}
        </span>
      </div>

      {trilha}

      <div className="mt-2 flex justify-between gap-1">
        {segs.map((s, i) => {
          const done = i < idx, active = i === idx;
          return (
            <div key={s.key} className={`flex-1 text-center text-[10px] font-medium truncate ${
              done ? 'text-emerald-600 dark:text-emerald-400'
              : active ? 'text-blue-700 dark:text-blue-300'
              : 'text-slate-400 dark:text-slate-500'
            }`}>
              <span className="mr-0.5">{done ? '✓' : active ? '●' : '○'}</span>{s.label}
            </div>
          );
        })}
      </div>

      <div className="mt-3 space-y-1">
        <p className="text-[11px] text-slate-600 dark:text-slate-300 truncate" title={msg || undefined}>
          {statusMsg}
          {segs.length > 1 && <span className="text-slate-400 dark:text-slate-500"> · etapa {idx + 1}/{segs.length}</span>}
        </p>
        <p className="text-[10px] leading-snug text-slate-400 dark:text-slate-500">
          Pode levar alguns minutos. Você pode fechar esta janela — o processo continua
          e o card avisa quando terminar.
        </p>
      </div>
    </div>
  );
}
